/**
 * OpCodeDescriptionSelect Component
 * 
 * A searchable dropdown for selecting op codes by their description or code.
 * Fetches from timetracker.op_codes table based on project and discipline.
 * 
 * Features:
 * - Typeahead search filtering by both op code and description
 * - Shows op_code and op_description in dropdown rows
 * - Selecting from dropdown auto-populates both op_code and op_description via onChange
 * - Requires jobId (project ID) and optionally disciplineCode to fetch codes
 * - Uses fixed positioning to escape overflow containers
 * - Once selected, shows as a "chip" with X to clear (not editable)
 * - displayField prop controls what the chip shows:
 *   'combined' (default) = op_code badge + description
 *   'op_code' = only op code
 *   'op_description' = only description
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const DROPDOWN_MIN_WIDTH = 480;

const OpCodeDescriptionSelect = ({ 
  value,          // { op_code, op_description }
  onChange,       // Callback when selection changes
  jobId,          // Required: project ID to fetch op codes for
  disciplineCode, // Optional: discipline code (e.g., 'PFS', 'MWR') to filter op codes
  displayField = 'combined', // 'combined' | 'op_code' | 'op_description' — controls chip display
  placeholder = 'Search by code or description...',
  disabled = false,
  style = {}
}) => {
  const { instance, accounts } = useMsal();
  const [searchValue, setSearchValue] = useState(''); // For searching when not selected
  const [allOpCodes, setAllOpCodes] = useState([]); // All op codes for the job
  const [filteredOpCodes, setFilteredOpCodes] = useState([]); // Filtered suggestions
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Check if we have a valid selection
  const hasSelection = value && value.op_code && value.op_code.trim() !== '';

  // Reset search value when selection changes
  useEffect(() => {
    if (!hasSelection) {
      // No selection, keep search value as is for typing
    } else {
      // Has selection, clear search value
      setSearchValue('');
    }
  }, [hasSelection]);

  // Calculate dropdown position based on input element
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, DROPDOWN_MIN_WIDTH);
      const margin = 12;
      let left = rect.left;
      if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - width);
      }
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: left + window.scrollX,
        width
      });
    }
  }, []);

  // Update position when opening or on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      const handleScrollOrResize = () => {
        updateDropdownPosition();
      };
      
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Fetch all op codes for the project/discipline when jobId or disciplineCode changes
  const fetchOpCodes = useCallback(async () => {
    if (!jobId) {
      setAllOpCodes([]);
      setFilteredOpCodes([]);
      return;
    }

    setLoading(true);
    try {
      // Build URL with optional disciplineCode filter; only fetch active op codes for dropdown
      let url = `/op-codes?jobId=${jobId}&activeOnly=true`;
      if (disciplineCode) {
        url += `&disciplineCode=${encodeURIComponent(disciplineCode)}`;
      }
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        url
      );

      if (response.data.success) {
        const codes = response.data.data || [];
        setAllOpCodes(codes);
        setFilteredOpCodes(codes);
      }
    } catch (error) {
      console.error('Error fetching op codes for project/discipline:', error);
      setAllOpCodes([]);
      setFilteredOpCodes([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobId, disciplineCode]);

  // Fetch op codes when jobId or disciplineCode changes
  useEffect(() => {
    fetchOpCodes();
  }, [fetchOpCodes]);

  // Filter suggestions based on search input
  useEffect(() => {
    if (!searchValue) {
      setFilteredOpCodes(allOpCodes);
    } else {
      const query = searchValue.toLowerCase();
      const filtered = allOpCodes.filter(oc => 
        (oc.op_description && oc.op_description.toLowerCase().includes(query)) ||
        (oc.op_code && oc.op_code.toLowerCase().includes(query))
      );
      setFilteredOpCodes(filtered);
    }
  }, [searchValue, allOpCodes]);

  // Handle search input change (only when no selection)
  const handleInputChange = (e) => {
    if (hasSelection) return; // Don't allow editing when selected
    setSearchValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle selection
  const handleSelect = (opcode) => {
    setSearchValue('');
    setIsOpen(false);
    onChange({
      op_code: opcode.op_code,
      op_description: opcode.op_description || '',
      is_extra: opcode.is_extra || false
    });
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSearchValue('');
    onChange({
      op_code: '',
      op_description: ''
    });
    // Focus the input after clearing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    // If selected, only allow Tab to move focus
    if (hasSelection) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault(); // Prevent editing
      }
      return;
    }

    if (!isOpen || filteredOpCodes.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOpCodes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOpCodes.length) {
          handleSelect(filteredOpCodes[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle focus
  const handleFocus = () => {
    if (!hasSelection && allOpCodes.length > 0) {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  // Handle container click (to open dropdown when clicking on selected chip)
  const handleContainerClick = () => {
    if (!hasSelection && !disabled && jobId) {
      updateDropdownPosition();
      setIsOpen(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  // Render dropdown using portal to escape overflow containers
  const renderDropdown = () => {
    if (!isOpen || hasSelection) return null;

    const dropdownContent = (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: '250px',
          overflowY: 'auto',
          zIndex: 99999
        }}
      >
        {filteredOpCodes.length > 0 ? (
          filteredOpCodes.map((opcode, index) => (
            <div
              key={opcode.id || opcode.op_code}
              onClick={() => handleSelect(opcode)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: highlightedIndex === index ? '#e9ecef' : 'white',
                borderBottom: index < filteredOpCodes.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Op Code - prominent on left */}
                <div style={{ 
                  fontSize: '0.85rem', 
                  color: '#0d6efd',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  backgroundColor: '#e7f1ff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  minWidth: '80px',
                  textAlign: 'center',
                  flexShrink: 0
                }}>
                  {opcode.op_code}
                </div>
                {/* Extra badge */}
                {opcode.is_extra && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#92400e',
                    fontWeight: '600',
                    backgroundColor: '#fef3c7',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    flexShrink: 0,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Extra
                  </div>
                )}
                {/* Description */}
                <div style={{ 
                  flex: 1,
                  fontSize: '0.9rem',
                  color: '#333',
                  lineHeight: 1.35,
                  whiteSpace: 'normal'
                }}>
                  {opcode.op_description || '(No description)'}
                </div>
              </div>
            </div>
          ))
        ) : !loading && jobId ? (
          <div style={{
            padding: '12px',
            color: '#6c757d',
            fontSize: '0.85rem',
            textAlign: 'center'
          }}>
            {allOpCodes.length === 0 
              ? 'No op codes configured for this project' 
              : 'No matching op codes found'
            }
          </div>
        ) : null}
      </div>
    );

    // Use portal to render dropdown at document body level
    return ReactDOM.createPortal(dropdownContent, document.body);
  };

  return (
    <div 
      ref={containerRef}
      onClick={handleContainerClick}
      style={{ 
        position: 'relative', 
        width: '100%',
        ...style 
      }}
    >
      {hasSelection ? (
        // Selected state: show as chip — content depends on displayField
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #28a745',
            borderRadius: '4px',
            fontSize: '0.85rem',
            boxSizing: 'border-box',
            backgroundColor: '#d4edda',
            color: '#155724',
            gap: '8px'
          }}
        >
          {/* displayField='op_code': show only the op code */}
          {displayField === 'op_code' && (
            <span style={{
              flex: 1,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              fontWeight: '600',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {value.op_code}
            </span>
          )}
          {/* displayField='op_description': show only the description */}
          {displayField === 'op_description' && (
            <span style={{ 
              flex: 1, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap'
            }}>
              {value.op_description || '(No description)'}
            </span>
          )}
          {/* displayField='combined' (default): show op_code badge + description */}
          {displayField === 'combined' && (
            <>
              <span style={{
                backgroundColor: '#155724',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                fontWeight: '600',
                flexShrink: 0
              }}>
                {value.op_code}
              </span>
              {value.is_extra && (
                <span style={{
                  fontSize: '0.65rem',
                  color: '#92400e',
                  fontWeight: '600',
                  backgroundColor: '#fef3c7',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  flexShrink: 0,
                  textTransform: 'uppercase'
                }}>
                  Extra
                </span>
              )}
              <span style={{ 
                flex: 1, 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap'
              }}>
                {value.op_description || ''}
              </span>
            </>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                padding: 0,
                border: 'none',
                borderRadius: '50%',
                backgroundColor: '#155724',
                color: 'white',
                cursor: 'pointer',
                fontSize: '12px',
                lineHeight: 1,
                flexShrink: 0
              }}
              title="Clear selection"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        // Not selected: show search input
        <>
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder={!jobId ? 'Select project first...' : placeholder}
            disabled={disabled || !jobId}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '0.85rem',
              boxSizing: 'border-box',
              backgroundColor: (disabled || !jobId) ? '#e9ecef' : 'white'
            }}
            autoComplete="off"
          />
          
          {loading && (
            <div style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.8rem',
              color: '#6c757d'
            }}>
              ...
            </div>
          )}
        </>
      )}

      {renderDropdown()}
    </div>
  );
};

export default OpCodeDescriptionSelect;
